/**
 * OrderDeleteButton — reusable header action for per-brand Order Detail pages.
 *
 * Fires POST /api/staff/orders/{product}/{id}/delete (soft delete), shows a
 * 5-second sonner Undo toast, and navigates back to the Invoice Board on
 * success. Only rendered for admins + managers; hidden for team and installers.
 */
import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { formatApiError } from '@/utils/apiError';

const OrderDeleteButton = ({ order, product, backTo = '/staff/orders', className = '' }) => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const canDelete = ['admin', 'manager'].includes(user?.role);
  const [busy, setBusy] = useState(false);

  if (!canDelete || !order?.id) return null;

  const restore = async () => {
    try {
      const res = await fetch(
        `${API_URL}/api/staff/orders/${product}/${order.id}/restore`,
        { method: 'POST', credentials: 'include' },
      );
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Restore failed');
      toast.success(`${order.reference || 'Order'} restored`);
      navigate(product === 'material_order' ? `/material-orders/${order.id}` : `/${product}/orders/${order.id}`);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleDelete = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch(
        `${API_URL}/api/staff/orders/${product}/${order.id}/delete`,
        { method: 'POST', credentials: 'include' },
      );
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Delete failed');
      toast(`${order.reference || 'Order'} moved to Recently Deleted`, {
        duration: 5000,
        action: { label: 'Undo', onClick: restore },
      });
      navigate(backTo);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={handleDelete}
      disabled={busy}
      data-testid="order-delete-btn"
      className={`bg-white/10 border-white/30 text-white hover:bg-rose-600/90 hover:border-rose-500 ${className}`}
      title="Delete order (soft delete — recoverable for 14 days)"
    >
      <Trash2 className="w-4 h-4 sm:mr-1" />
      <span className="hidden sm:inline">{busy ? 'Deleting…' : 'Delete'}</span>
    </Button>
  );
};

export default OrderDeleteButton;
